<template>
  <mia-form
    class="overflow-hidden"
    @submit="$emit('submit')"
  >
    <mia-panel
      title="Schnellsuche"
      class="bg-white"
      :is-collapsed="isCollapsed"
      :without-title="inline"
      :seamless="inline"
    >
      <template #title="{ title }">
        <div class="flex items-center justify-between px-4 py-3 -mx-4 -my-3">
          <span
            class="inline-block w-full cursor-pointer"
            @click="$emit('toggle')"
            v-text="title"
          />

          <mia-collapsible-toggle-button
            :collapsed="isCollapsed"
            class="focus:outline-none"
            @click="$emit('toggle')"
          />
        </div>
      </template>

      <div class="grid grid-cols-2 gap-8 p-4">
        <!-- FIN -->
        <div>
          <mia-feature-input
            v-model="fin"
            label="FIN"
          />
        </div>

        <!-- Commission Number -->
        <div>
          <mia-feature-input
            v-model="commissionNumber"
            label="Kommissionsnummer"
          />
        </div>

        <!-- ID -->
        <div class="col-span-2">
          <div class="grid grid-cols-2 gap-8">
            <mia-feature-input
              v-model="id"
              label="Fahrzeug-ID"
            />
          </div>
        </div>

        <!-- Type -->
        <div>
          <mia-feature-label text="Fahrzeugtyp" />

          <mia-type-select
            v-model="type"
            class="w-full"
            without-label
            :columns="3"
          />
        </div>

        <!-- Status -->
        <div>
          <mia-feature-label text="Status" />

          <mia-status-select
            v-model="status"
            class="w-full"
            without-label
            :columns="3"
          />
        </div>

        <!-- Make -->
        <div>
          <mia-make-select
            v-model="make"
            class="w-full"
          />
        </div>

        <!-- Model -->
        <div>
          <mia-model-select
            v-model="model"
            :make="make"
            class="w-full"
          />
        </div>

        <!-- Price Customer From -->
        <div>
          <mia-feature-input
            v-slot="{ id }"
            label="Angebotspreis ab"
          >
            <mia-gross-net-input
              :id="id"
              v-model="priceCustomerFrom"
              mode="net"
              :is-differential-taxed="false"
              :purchasing-price="null"
            />
          </mia-feature-input>
        </div>

        <!-- Price Customer To -->
        <div>
          <mia-feature-input
            v-slot="{ id }"
            label="Angebotspreis bis"
          >
            <mia-gross-net-input
              :id="id"
              v-model="priceCustomerTo"
              mode="net"
              :is-differential-taxed="false"
              :purchasing-price="null"
            />
          </mia-feature-input>
        </div>

        <!-- Initial Registration From -->
        <div>
          <mia-feature-input
            v-model="initialRegistrationFrom"
            label="Erstzulassung ab"
            type="date"
            :max="initialRegistrationTo || ''"
          />
        </div>

        <!-- Initial Registration To -->
        <div>
          <mia-feature-input
            v-model="initialRegistrationTo"
            label="Erstzulassung bis"
            type="date"
            :min="initialRegistrationFrom || ''"
          />
        </div>

        <!-- Mileage From -->
        <div>
          <mia-feature-input
            v-model="mileageFrom"
            type="number"
            label="Kilometerstand von"
            min="0"
            :max="parseInt(mileageTo, 10) > 0 ? parseInt(mileageTo, 10) : null"
          />
        </div>

        <!-- Mileage To -->
        <div>
          <mia-feature-input
            v-model="mileageTo"
            type="number"
            label="Kilometerstand bis"
            :min="parseInt(mileageTo, 10) > 0 ? parseInt(mileageTo, 10) : null"
          />
        </div>
      </div>

      <template #controls>
        <div class="flex items-center justify-between flex-1">
          <button
            v-if="canChangeMode"
            class="inline-flex items-center h-12 px-6 py-3 text-sm transition-colors duration-100 ease-out bg-orange-100 border border-orange-300 rounded shadow text-orange-mia focus:outline-none focus:shadow-outline hover:bg-orange-mia hover:text-white"
            type="button"
            @click="$emit('change-mode')"
          >
            Detailsuche
          </button>
          <div v-else />

          <slot name="custom-controls" />

          <div class="flex">
            <mia-cancel-button
              :disabled="$wait.any"
              label="Suche zurücksetzen"
              class="bg-transparent"
              @click="$emit('reset')"
            />

            <mia-submit-button
              :is-loading="$wait.any"
              label="Suchen"
              class="ml-3"
            />
          </div>
        </div>
      </template>
    </mia-panel>
  </mia-form>
</template>

<script>
import CancelButton from '@/components/cars/edit/CancelButton.vue'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import FeatureLabel from '@/components/cars/edit/FeatureLabel.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import MakeSelect from '@/components/cars/inputs/MakeSelect.vue'
import ModelSelect from '@/components/cars/inputs/ModelSelect.vue'
import StatusSelect from '@/components/cars/inputs/StatusSelect.vue'
import TypeSelect from '@/components/cars/inputs/TypeSelect.vue'
import CollapsibleToggleButton from '@/components/ui/CollapsibleToggleButton.vue'
import GrossNetInput from '@/components/ui/inputs/GrossNetInput.vue'
import Form from '@/components/ui/widgets/Form.vue'
import Panel from '@/components/ui/widgets/Panel.vue'

export default {
  name: 'MiaQuickSearch',

  components: {
    'mia-cancel-button': CancelButton,
    'mia-collapsible-toggle-button': CollapsibleToggleButton,
    'mia-feature-input': FeatureInput,
    'mia-feature-label': FeatureLabel,
    'mia-form': Form,
    'mia-gross-net-input': GrossNetInput,
    'mia-make-select': MakeSelect,
    'mia-model-select': ModelSelect,
    'mia-panel': Panel,
    'mia-submit-button': SubmitButton,
    'mia-status-select': StatusSelect,
    'mia-type-select': TypeSelect
  },

  props: {
    isCollapsed: {
      type: Boolean,
      default: false
    },

    inline: {
      type: Boolean,
      default: false
    },

    canChangeMode: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    fin: {
      get() {
        return this.$store.state.search.form.fin
      },

      set(value) {
        this.$store.commit('search/setFormField', { field: 'fin', value })
      }
    },

    commissionNumber: {
      get() {
        return this.$store.state.search.form.commissionNumber
      },

      set(value) {
        this.$store.commit('search/setFormField', { field: 'commissionNumber', value })
      }
    },

    make: {
      get() {
        return this.$store.state.search.form.makes[0].make
      },

      set(value) {
        this.$store.commit('search/setMake', { index: 0, field: 'make', value })
      }
    },

    model: {
      get() {
        return this.$store.state.search.form.makes[0].model
      },

      set(value) {
        this.$store.commit('search/setMake', { index: 0, field: 'model', value })
      }
    },

    priceCustomerFrom: {
      get() {
        return this.$store.state.search.form.priceCustomerFrom
      },

      set(value) {
        this.$store.commit('search/setFormField', { field: 'priceCustomerFrom', value })
      }
    },

    priceCustomerTo: {
      get() {
        return this.$store.state.search.form.priceCustomerTo
      },

      set(value) {
        this.$store.commit('search/setFormField', { field: 'priceCustomerTo', value })
      }
    },

    initialRegistrationFrom: {
      get() {
        return this.$store.state.search.form.initialRegistrationFrom
      },

      set(value) {
        this.$store.commit('search/setFormField', { field: 'initialRegistrationFrom', value })
      }
    },

    initialRegistrationTo: {
      get() {
        return this.$store.state.search.form.initialRegistrationTo
      },

      set(value) {
        this.$store.commit('search/setFormField', { field: 'initialRegistrationTo', value })
      }
    },

    mileageFrom: {
      get() {
        return this.$store.state.search.form.mileageFrom
      },

      set(value) {
        this.$store.commit('search/setFormField', { field: 'mileageFrom', value })
      }
    },

    mileageTo: {
      get() {
        return this.$store.state.search.form.mileageTo
      },

      set(value) {
        this.$store.commit('search/setFormField', { field: 'mileageTo', value })
      }
    },

    id: {
      get() {
        return this.$store.state.search.form.id
      },

      set(value) {
        this.$store.commit('search/setFormField', { field: 'id', value })
      }
    },

    status: {
      get() {
        return this.$store.state.search.form.status
      },

      set(value) {
        this.$store.commit('search/setFormField', { field: 'status', value })
      }
    },

    type: {
      get() {
        return this.$store.state.search.form.type
      },

      set(value) {
        this.$store.commit('search/setFormField', { field: 'type', value })
      }
    }
  }
}
</script>
